import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {registerLocale} from "react-datepicker";
import {ptBR} from "date-fns/locale/pt-BR";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import TruncateCellRenderer from "../components/Truncate.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import {useQuery} from "@tanstack/react-query";
import moment from "moment";
import AlertMessages from "../../../../components/messages/alert-message.js";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFile, FaPaperclip, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import bosService from "../../../../services/web/bos-service.js";
import boKeys from "../../../../components/messages/translatedBOKeys.js";
import {Modal} from "../../../../components/modal/modal.jsx";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {useSearchParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import SmartSearchArea from "./smart-search-area.jsx";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import ShowHideColumnsGrid from "../../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";

const BASENAME = import.meta.env.BASE_URL;

const defaultStatusFilter = [
    'NUMBER_GENERATED',
    'DRAFT',
    'FILE_SENT',
    'REVIEWED',
    'APPROVED',
    'PENDING',
    'REJECTED',
];

const initialFilters = {
    boId: "",
    related_agent: null,
    user_approval_namewar: "",
    status: [
        {value: "NUMBER_GENERATED", label: "Numeração gerada"},
        {value: "DRAFT", label: "Em preenchimento"},
        {value: "FILE_SENT", label: "Documento enviado"},
        {value: "PENDING", label: "Pendente (aprovação)"},
        {value: "REVIEWED", label: "Revisado"},
        {value: "REJECTED", label: "Reprovado"},
        {value: "APPROVED", label: "Aprovado"},
    ],
    dtStart: "",
    dtEnd: "",
    integrant: null,
    tags: [],
    leader: [],
    address: "",
    reason: [],
    other_document: [],
    keyword: "",
    homeless: null,
    teams: ""
}

const homelessOpt = [
    {value: "true", label: "Sim"},
    {value: "false", label: "Não"}
];

const statusOpt = [
    {value: "NUMBER_GENERATED", label: "Numeração gerada"},
    {value: "DRAFT", label: "Em preenchimento"},
    {value: "APP_DELETED", label: "Excluído (app)"},
    {value: "SYSTEM_DELETED", label: "Excluído (sistema)"},
    {value: "FILE_SENT", label: "Documento enviado"},
    {value: "PENDING", label: "Pendente (aprovação)"},
    {value: "REVIEWED", label: "Revisado"},
    {value: "REJECTED", label: "Reprovado"},
    {value: "APPROVED", label: "Aprovado"},
    {value: "INVALIDATED", label: "Invalidado"}
]

const BosList = () => {
    const [state, setState] = useState(initialFilters);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreatingBo, setIsCreatingBo] = useState(false);
    const [columnDefs, setColumnDefs] = useState([]);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState({
        reason: [],
        other_document: [],
        integrant_agent: [],
        leader: [],
        teams: [],
    });
    const [lastRow, setLastRow] = useState("");
    const [bos, setBos] = useState([]);
    const [allowReport, setAllowReport] = useState("")
    const [allowCreateBo, setAllowCreateBo] = useState("")
    const [searchParams] = useSearchParams();
    const [lastClickedBoId, setLastClickedBoId] = useState(null);
    const [showSmartSearch, setShowSmartSearch] = useState(true);
    const [smartSearchText, setSmartSearchText] = useState("");
    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const [queryFilters, setQueryFilters] = useState(() => {
        const ids = searchParams.getAll("ids[]").map(id => id.replace("-", "/"));

        if (ids.length > 0) {
            return {filter: [{field: 'ids', type: 'in', value: ids}]}
        } else {
            return {filter: [{field: 'status', type: 'in', value: defaultStatusFilter}]}
        }

    });

    const gridRef = useRef();
    registerLocale('pt-BR', ptBR);

    const navigate = navigateHook();

    useEffect(() => {
        const ids = searchParams.getAll("ids[]").map(id => id.replace("-", "/"));

        if (ids.length > 0) {
            defaultQueryFilter = {
                filter: [
                    {
                        field: 'ids',
                        type: 'in',
                        value: ids
                    }
                ]
            }
        }
    }, []);


    useQuery({
        queryFn: async () => await bosService.indexPermissions().then((data) => {
            setAllowReport(data.allowReport)
            setAllowCreateBo(data.allowCreateBo)
            return []
        }),
        queryKey: ['indexPermissions']
    });

    const {data: boName} = useQuery({
        queryFn: () => bosService.getBoName(),
        queryKey: ['boName'],
        initialData: 'BO'
    });

    useQuery({
        queryFn: async () => await bosService.getTeams().then((data) => {
            formatToSelect(data, "teams");
            return [];
        }),
        queryKey: ['teams']
    });

    useEffect(() => {
        const columns = [
            {
                headerName: `${boName}`, field: "id", width: 90,
                comparator: (a, b) => {
                    return aggridUtils.agGridNumberYearComparator(a, b);
                }
            },
            {
                headerName: "D/H do fato", field: "dt_fact", width: 140,
                cellRenderer: TruncateCellRenderer,
                comparator: (a, b, nodeA, nodeB, isDescending) => {
                    return aggridUtils.agGridCompareDateTime(a, b, nodeA, nodeB, isDescending);
                }
            },
            {
                headerName: "Natureza(s)", field: "reason",
                valueGetter: (params) => {
                    const {reason} = params.data
                    if (reason.length > 1) {
                        return reason.join(', ');
                    }
                    return reason;
                },
                cellRenderer: TruncateCellRenderer,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Cadastro/edição", field: "system_user_namewar", width: 130,
                valueGetter: (params) => {
                    const {source, system_user_namewar, started_namewar, phone} = params.data

                    let sourceStr = '';

                    if (source === 'APP') sourceStr = 'App';
                    else if (source === 'UPLOAD_FILE') sourceStr = 'Envio de arquivo';
                    else if (source === 'WEB') sourceStr = 'Área web';

                    let text = sourceStr !== '' ?
                        `${(system_user_namewar === null || system_user_namewar === '') ? started_namewar : system_user_namewar} (${sourceStr})`
                        : system_user_namewar;

                    if (phone) text += " - " + projectUtils.formatPhone(phone);

                    return text;
                },
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Aprovado por", field: "user_approval_namewar", width: 115,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Revisado por", field: "user_review_namewar", width: 115,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Responsável", field: "leader",
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Status", field: "status", width: 150,
                valueFormatter: (params) => {
                    return boKeys[params.value] ?? params.value;
                }
            },
            {
                headerName: "Endereço", field: "address", minWidth: 140, flex: 1,
                valueFormatter: (params) => {
                    const data = params.value;
                    if (data === "  - ") {
                        return "";
                    } else {
                        return data
                    }
                },
                cellRenderer: (params) => {
                    const data = params.value;
                    if (data === "  - ") {
                        return ""
                    } else {
                        return <span className="truncate" title={data}>{data}</span>
                    }

                },
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    a = a === "  - " ? "" : a
                    b = b === "  - " ? "" : b
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "Croqui e fotos", field: "croquis", width: 120,
                cellRenderer: (params) => {
                    if (params.value != "") {
                        return <div className="flex my-3 justify-center items-center">
                            <FaPaperclip/>
                        </div>
                    }
                    return ""
                },
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "BO PC", field: "boPcs", width: 100,
                cellRenderer: TruncateCellRenderer,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            }
        ];

        const hiddenColumns = JSON.parse(localStorage.getItem("bos-hidden-columns"));

        if (hiddenColumns) {
            columns.forEach(c => hiddenColumns.includes(c.field) ? c.hide = true : "");
        } else {
            columns.forEach(c => (c.field === "user_review_namewar" || c.field === "croquis" || c.field === "boPcs") ? c.hide = true : "");
        }

        setColumnDefs(columns);
    }, [boName]);

    const toggleColumn = (field) => {
        setColumnDefs((prevDefs) =>
            prevDefs.map((col) => col.field === field ? {...col, hide: !col.hide} : col)
        );
    }

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field)
    }

    const {isLoading: isLoadingBos, refetch} = useQuery({
        queryFn: () => bosService.list(JSON.stringify(queryFilters)).then((data) => {
            setLastRow(data.last_row)
            setBos(data.data)

            return []
        }),
        queryKey: ['nature', queryFilters],
    });

    useQuery({
        queryFn: async () => await bosService.getNature().then((data) => {
            formatToSelect(data, "reason")
            return []
        }),
        queryKey: ['reason']
    });

    useQuery({
        queryFn: async () => await bosService.otherDocuments().then((data) => {
            setOptions(prev => ({...prev, other_document: data}))
            return []
        }),
        queryKey: ['other_document']
    });

    useQuery({
        queryFn: async () => await bosService.getNameWar().then((data) => {
            formatToSelect(data, "integrant_agent")
            return []
        }),
        queryKey: ['integrant_agent']
    });

    useQuery({
        queryFn: async () => await bosService.getLeaders().then((data) => {
            formatToSelect(data, "leader")
            return []
        }),
        queryKey: ['leader']
    });

    const {data: tagOptions = []} = useQuery({
        queryFn: () => bosService.getTagOptions(),
        queryKey: ['tags-options']
    });

    const formatToSelect = (data, index) => {
        const newData = data.map(item => ({
            value: item.id,
            label: item.text,
        }));
        setOptions(prev => ({...prev, [index]: newData}))
    }

    const onCellClicked = ({data, event}) => {
        const id = data.id.replace('/', '-');
        const url = `/bos/${data.id.replace('/', '-')}`;

        setLastClickedBoId(data.id);

        navigate("/bo/redirect/" + id, !event.ctrlKey)
    }

    const hide = () => {
        setOpen(!open)
    }

    const onChange = ({target: {value, id}}) => {
        setState(prev => ({...prev, [id]: value}));
    }

    const handleSelect = (id, value) => {
        setState(prev => ({...prev, [id]: value}));
    };

    const prepareSelect = (data, isMulti = false) => {
        let status = []

        if (isMulti) {
            data.map(item => {
                status.push(`${item.value}`);
            })
        } else {
            return `${data ? data.value : ""}`
        }

        return status
    }

    const formatSmartSearchFilters = (filters) => {
        const filterKeys = {
            reason_smart: "reason",
            other_document_smart: "other_document",
            related_agent_namewar: "related_agent",
            integrant_namewar: "integrant",
            leader_namewar: "leader",
        };

        const selectOptions = {
            related_agent: options.integrant_agent,
            status: statusOpt,
            integrant: options.integrant_agent,
            leader: options.leader,
            homeless: homelessOpt,
            reason: options.reason,
            other_document: options.other_document,
            tags: tagOptions,
        };

        const multiSelectFields = ["status", "leader", "reason", "other_document", "tags"];

        return Object.entries(filters ?? {}).reduce((formattedFilters, [key, value]) => {
            const stateKey = filterKeys[key] ?? key;
            if (!Object.hasOwn(initialFilters, stateKey)) return formattedFilters;

            if (key === "related_agent_namewar") {
                const relatedAgentNamewar = Array.isArray(value) ? value[0] : value;

                formattedFilters[stateKey] = relatedAgentNamewar
                    ? {
                        value: relatedAgentNamewar,
                        label: String(relatedAgentNamewar),
                        searchField: key,
                    }
                    : null;

                return formattedFilters;
            }

            const fieldOptions = selectOptions[stateKey];
            if (!fieldOptions) {
                formattedFilters[stateKey] = value;
                return formattedFilters;
            }

            const values = (Array.isArray(value) ? value : [value]).map(item =>
                stateKey === "other_document" && typeof item === "string"
                    ? item.charAt(0).toUpperCase() + item.slice(1)
                    : item
            );
            const selectedOptions = values
                .filter(item => item !== null && item !== undefined && item !== "")
                .map(item => {
                    if (typeof item === "object") {
                        if (stateKey !== "other_document") return item;

                        return fieldOptions.find(option =>
                            String(option.value) === String(item.value) || option.label === item.label
                        );
                    }

                    const selectedOption = fieldOptions.find(option =>
                        String(option.value) === String(item) || option.label === item
                    );

                    return selectedOption ?? (stateKey === "other_document"
                        ? null
                        : {value: item, label: String(item)});
                })
                .filter(Boolean);

            formattedFilters[stateKey] = multiSelectFields.includes(stateKey)
                ? selectedOptions
                : selectedOptions[0] ?? null;

            return formattedFilters;
        }, {status: []});
    }

    const search = () => {
        if (state.dtStart && !state.dtEnd) {
            projectUtils.swalErrorMessage('', 'O campo "Data/hora final do fato" é obrigatório.', 'Ok');
            return false;
        }

        if (state.dtEnd && !state.dtStart) {
            projectUtils.swalErrorMessage('', 'O campo "Data/hora inicial do fato" é obrigatório.', 'Ok');
            return false;
        }

        const dtStart = moment(state.dtStart);
        const dtEnd = moment(state.dtEnd);
        const diff = dtEnd.diff(dtStart, "days", true);

        if (diff < 0) {
            projectUtils.swalErrorMessage('', 'A data inicial do fato não pode ser maior que a data atual', 'Ok');
            return false;
        }

        setShowSmartSearch(false);
        setQueryFilters({
            filter: [
                {
                    field: 'id',
                    type: 'like',
                    value: state.boId
                },
                {
                    field: state.related_agent?.searchField ?? 'related_agent',
                    type: 'like',
                    value: prepareSelect(state.related_agent)
                },
                {
                    field: 'user_approval_namewar',
                    type: 'like',
                    value: state.user_approval_namewar
                },
                {
                    field: 'status',
                    type: 'in',
                    value: prepareSelect(state.status, true)
                },
                {
                    field: 'dtStart',
                    type: '=',
                    value: state.dtStart ? projectUtils.formatDate(state.dtStart) : ""
                },
                {
                    field: 'dtEnd',
                    type: '=',
                    value: state.dtEnd ? projectUtils.formatDate(state.dtEnd) : ""
                },
                {
                    field: 'reason',
                    type: 'in',
                    value: prepareSelect(state.reason, true)
                },
                {
                    field: 'other_document',
                    type: 'in',
                    value: prepareSelect(state.other_document, true)
                },
                {
                    field: 'leader_id',
                    type: 'in',
                    value: prepareSelect(state.leader, true)
                },
                {
                    field: 'integrant',
                    type: '=',
                    value: prepareSelect(state.integrant)
                },
                {
                    field: 'tags',
                    type: 'in',
                    value: prepareSelect(state.tags, true)
                },
                {
                    field: 'keyword',
                    type: 'like',
                    value: state.keyword
                },
                {
                    field: 'address',
                    type: 'like',
                    value: state.address
                },
                {
                    field: 'homeless',
                    type: '=',
                    value: prepareSelect(state.homeless)
                },
                {
                    field: 'team',
                    type: '=',
                    value: prepareSelect(state.teams)
                },
            ]
        });
    }

    const smartSearch = () => {
        const query = smartSearchText.trim();

        if (!query) return;

        setIsLoading(true);
        bosService.smartSearch({query})
            .then((data) => {
                const records = data?.data ?? [];

                setState(prev => ({...prev, ...formatSmartSearchFilters(data?.filters)}));
                setBos(records);
                setLastRow(data?.last_row ?? data?.total ?? records.length);
                setShowSmartSearch(false);
            })
            .catch((error) => {
                console.error(error);
                projectUtils.swalErrorMessage("", "Não foi possível realizar a pesquisa inteligente.", "Ok");
            })
            .finally(() => setIsLoading(false));
    }

    const newBo = () => {
        setIsCreatingBo(true)
        bosService.createBogcm()
            .then((data) => {
                if (data.id) {
                    projectUtils.success(true, "Adicionado com sucesso");
                    navigate(`/bo/form/${data.id.replace('/', '-')}`)
                    setTimeout(() => setIsLoading(false), 1000)
                }

                setIsCreatingBo(false)
            })
            .catch((error) => {
                console.error(error);

                const {message} = error.response.data;

                let errMessage = AlertMessages[message] ?? `Não foi possível gerar um número de ${boName}`;
                projectUtils.swalErrorMessage("", errMessage, "Ok");
                setIsCreatingBo(false)
            });
    }

    const sendToReport = () => {
        window.open(`${BASENAME}reports/list_bos?without-status=1`);
    }

    const openSmartSearchArea = () => {
        setState(initialFilters)
        setShowSmartSearch(true);
    }

    const clearFields = () => {
        setState(initialFilters)
        setSmartSearchText("");
        openSmartSearchArea();
        setQueryFilters({filter: [{field: 'status', type: 'in', value: defaultStatusFilter}]})
    }

    const showLastRecords = () => {
        setState(initialFilters);
        setSmartSearchText("");
        setShowSmartSearch(false);
        setQueryFilters({filter: [{field: 'status', type: 'in', value: defaultStatusFilter}]});
    }

    useEffect(() => {
        refetch()
    }, [queryFilters]);

    const handleDate = useCallback((value, field) => {
        setState(prev => ({
            ...prev,
            [field]: value || null
        }));
    }, []);

    const getSelectValues = (items) => (items ?? []).map(item => item.value).sort().join("|");
    const hasActiveFilters = useMemo(() => {
        const hasStatusChanged = getSelectValues(state.status) !== getSelectValues(initialFilters.status);

        return Boolean(
            state.boId ||
            state.related_agent ||
            state.user_approval_namewar ||
            hasStatusChanged ||
            state.dtStart ||
            state.dtEnd ||
            state.integrant ||
            (state.tags ?? []).length ||
            (state.leader ?? []).length ||
            state.address ||
            (state.reason ?? []).length ||
            (state.other_document ?? []).length ||
            state.keyword ||
            state.homeless
        );
    }, [state]);
    const isSmartSearchActive = showSmartSearch && Boolean(smartSearchText.trim());
    const submitSearch = () => {
        if (isSmartSearchActive) {
            smartSearch();
            return;
        }

        search();
    }

    useGlobalKey("Enter", submitSearch);
    useGlobalKey("Escape", clearFields);

    const getRowId = useCallback((params) => params.data?.id, []);

    const rowClassRules = useMemo(() => {
        return {
            "bo-last-clicked-row": (params) => params.data?.id === lastClickedBoId,
        };
    }, [lastClickedBoId]);

    return (
        <Template isLoading={isLoading || isLoadingBos}>
            <Template.Header>
                <Template.Title>{boName}</Template.Title>
                <div className={"flex gap-2"}>
                    {allowReport &&
                        <Button onClick={sendToReport}>
                            <FaFile/> Relatório
                        </Button>
                    }
                    {allowCreateBo &&
                        <Button onClick={() => setOpen(!open)}>
                            <FaPlus/> Adicionar {boName}
                        </Button>
                    }
                </div>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-3">
                                <InputForm id="boId" label={`Número do ${boName}`} type="search" value={state.boId}
                                           onChange={onChange} disabled={isSmartSearchActive}/>
                            </div>
                            <div className={"col-span-3"}>
                                <SelectComponent
                                    name="related_agent"
                                    value={state.related_agent}
                                    options={options.integrant_agent}
                                    label={"Agente relacionado"}
                                    onChange={(value) => handleSelect("related_agent", value)}
                                    isDisabled={isSmartSearchActive}
                                />
                            </div>
                            <div className="col-span-3">
                                <InputForm id="user_approval_namewar" label={"Aprovado por"} type="search"
                                           value={state.user_approval_namewar}
                                           onChange={onChange} disabled={isSmartSearchActive}/>
                            </div>
                            <div className={"col-span-3"}>
                                <SelectComponent
                                    name="status"
                                    value={state.status}
                                    options={statusOpt}
                                    label={"Status"}
                                    onChange={(value) => handleSelect("status", value)}
                                    isDisabled={isSmartSearchActive}
                                    isMulti
                                />
                            </div>

                            <div
                                className="flex items-center col-span-3">
                                <div className="relative flex-1">
                                    <InputDateTime
                                        id={"dtStart"}
                                        selected={state.dtStart}
                                        label={"Fato entre"}
                                        onChange={(date) => handleDate(date, "dtStart")}
                                        classNameWrapper={"right-date-input"}
                                        disabled={isSmartSearchActive}
                                        customIcon
                                    />
                                </div>
                                <div
                                    className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">
                                    à
                                </div>
                                <div className="relative flex-1">
                                    <InputDateTime
                                        id={"dtEnd"}
                                        selected={state.dtEnd}
                                        label={""}
                                        onChange={(date) => handleDate(date, "dtEnd")}
                                        classNameWrapper={"left-date-input"}
                                        disabled={isSmartSearchActive}
                                    />
                                </div>
                            </div>

                            <div className={"col-span-3"}>
                                <SelectComponent
                                    name="integrant"
                                    value={state.integrant}
                                    options={options.integrant_agent}
                                    label={"Integrante"}
                                    onChange={(value) => handleSelect("integrant", value)}
                                    isDisabled={isSmartSearchActive}
                                />
                            </div>
                            <div className={"col-span-3"}>
                                <SelectComponent
                                    name="leader"
                                    value={state.leader}
                                    options={options.leader}
                                    label={"Responsável"}
                                    onChange={(value) => handleSelect("leader", value)}
                                    isDisabled={isSmartSearchActive}
                                    isMulti
                                />
                            </div>

                            <div className={"col-span-3"}>
                                <SelectComponent
                                    name="homeless"
                                    value={state.homeless}
                                    options={homelessOpt}
                                    label={"Situação de rua"}
                                    onChange={(value) => handleSelect("homeless", value)}
                                    isDisabled={isSmartSearchActive}
                                />
                            </div>

                            <div className="col-span-6">
                                <InputForm id="address" label={"Endereço"} type="search" value={state.address}
                                           onChange={onChange} disabled={isSmartSearchActive}/>
                            </div>
                            <div className={"col-span-3"}>
                                <SelectComponent
                                    name="other_document"
                                    value={state.other_document}
                                    options={options.other_document}
                                    label={"Documentos"}
                                    onChange={(value) => handleSelect("other_document", value)}
                                    isDisabled={isSmartSearchActive}
                                    isMulti
                                />
                            </div>
                            <SelectComponent
                                classNameWrapper="col-span-3"
                                label="Equipe"
                                options={options.teams}
                                value={state.teams}
                                isDisabled={isSmartSearchActive}
                                onChange={(value) => handleSelect("teams", value)}

                            />
                            <div className={"col-span-6"}>
                                <SelectComponent
                                    name="reason"
                                    value={state.reason}
                                    options={options.reason}
                                    label={"Natureza"}
                                    onChange={(value) => handleSelect("reason", value)}
                                    isDisabled={isSmartSearchActive}
                                    isMulti
                                />
                            </div>
                            <div className={"col-span-6"}>
                                <SelectComponent
                                    id="tags"
                                    name="tags"
                                    value={state.tags}
                                    options={tagOptions}
                                    label={"Marcadores"}
                                    onChange={(value) => handleSelect("tags", value)}
                                    isMulti
                                />
                            </div>
                            <div className="col-span-12 flex gap-4">
                                <InputForm classNameWrapper="w-full" id="keyword" label={"Texto contido"} type="search"
                                           value={state.keyword} onChange={onChange} disabled={isSmartSearchActive}/>
                                <div className={"flex gap-2"}>
                                    <Button className={"w-8 !px-0"} title="Limpar" onClick={() => clearFields()}
                                            target={"primary-outline"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button title="Pesquisa inteligente" onClick={openSmartSearchArea}
                                            className={"w-8 !px-0"}>
                                        <img src={getAssetPath("img/bos-list/icon-search-btn.svg")} alt=""
                                             className="w-3.75" aria-hidden="true"/>
                                    </Button>
                                    <Button className={"w-8 !px-0"} title="Buscar" onClick={() => search()}
                                            disabled={isSmartSearchActive}>
                                        <FaSearch/>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {
                    showSmartSearch ?
                        <SmartSearchArea
                            value={smartSearchText}
                            onChange={setSmartSearchText}
                            onSearch={smartSearch}
                            onShowLastRecords={showLastRecords}
                            disabled={hasActiveFilters}
                            isSearching={isLoading}
                        />
                        :
                        <Card className="mt-2">
                            <Card.Body>
                                <div
                                    className={`${lastRow > 500 ? "h-[calc(100vh-341px)]" : "h-[calc(100vh-297px)]"} aggrid-row-pointer`}>
                                    <AgGridReact
                                        ref={gridRef}
                                        rowData={bos}
                                        columnDefs={columnDefs}
                                        pagination
                                        paginationPageSize={100}
                                        paginationPageSizeSelector={[20, 50, 100, 500]}
                                        animateRows={false}
                                        suppressCellFocus={true}
                                        onCellClicked={onCellClicked}
                                        onColumnVisible={onColumnVisible}
                                        gridOptions={{localeText: aggridUtils.localization()}}
                                        theme={theme}
                                        getRowId={getRowId}
                                        rowClassRules={rowClassRules}
                                    />
                                </div>

                                <ShowHideColumnsGrid
                                    columnDefs={columnDefs}
                                    toggleColumn={toggleColumn}
                                    storageItem={"bos-hidden-columns"}
                                    buttonClassName={!showSmartSearch && lastRow > 500 ? "bottom-[58px]!" : ""}
                                />

                                {
                                    !showSmartSearch && lastRow > 500 &&
                                    <div className="flex p-2 rounded-sm bg-[#FBF0DA] mt-2">
                                        {`Limite de 500 registros atingido. Exibindo ${bos?.length} de ${lastRow}.`}
                                    </div>
                                }
                            </Card.Body>
                        </Card>
                }

                <Modal open={open} setOpen={setOpen} title={`Deseja cadastrar um ${boName}?`} className={"w-4/12"}>
                    <Modal.Body className="p-2">
                        <div className="mb-2 flex text-lg justify-center">
                            <p>Um novo número de {boName} será gerado.</p>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            onClick={newBo}
                            actionFeedbackType="save"
                            loading={isCreatingBo}
                            loadingText="Gerando..."
                            successText="Adicionado com sucesso"
                        >
                            Confirmar
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Template.Body>
        </Template>
    );
};

export default BosList;